import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { MutableRefObject, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';

const FormData = z.object({
  domainUrl: z
    .string()
    .min(1, t('ssoDomainUrlRequired'))
    .regex(/^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/, t('ssoDomainUrlInvalid')),
});
type FormData = z.infer<typeof FormData>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onRegister: (domainUrl: string) => Promise<void> | void;
}

export function RegisterDomainModal({ isOpen, onClose, onRegister }: Props) {
  const initialFocusRef = useRef<HTMLInputElement | null>(null);

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <RegisterDomainModalContent
          onRegister={onRegister}
          onClose={onClose}
          initialFocusRef={initialFocusRef}
        />
      </ModalContent>
    </Modal>
  );
}

interface ContentProps {
  onRegister: (domainUrl: string) => Promise<void> | void;
  initialFocusRef: MutableRefObject<HTMLInputElement | null>;
  onClose: () => void;
}

function RegisterDomainModalContent({ onRegister, initialFocusRef, onClose }: ContentProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      domainUrl: '',
    },
    mode: 'onChange',
    resolver: zodResolver(FormData),
  });
  const { ref: domainInputRef, ...domainInputProps } = register('domainUrl');

  const onSubmit = useCallback(
    async ({ domainUrl }: FormData) => {
      await onRegister(domainUrl);
      onClose();
    },
    [onRegister, onClose],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>{t('registerNewDomain')}</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      <ModalBody>
        <Stack spacing={4}>
          <Text fontSize="sm" color="gray.600">
            {t('registerDomainDescription')}
          </Text>
          <FormControl isRequired isInvalid={Boolean(errors.domainUrl)}>
            <FormLabel>{t('organisationUrl')}</FormLabel>
            <Input
              placeholder="www.company.com"
              ref={(r) => {
                domainInputRef(r);
                initialFocusRef.current = r;
              }}
              {...domainInputProps}
            />
            <FormErrorMessage>{errors.domainUrl?.message}</FormErrorMessage>
          </FormControl>
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" colorScheme="gray" onClick={onClose} isDisabled={isSubmitting}>
          {t('cancel')}
        </Button>
        <Button
          variant="solid"
          colorScheme="blue"
          marginLeft="3"
          type="submit"
          isDisabled={!isDirty || isSubmitting || Object.keys(errors).length > 0}
          isLoading={isSubmitting}
        >
          {t('registerNewDomain')}
        </Button>
      </ModalFooter>
    </form>
  );
}
